import { api } from 'shared/api/api';
import {
	action,
	makeObservable,
	observable,
	override,
	runInAction
} from 'mobx';
import { EntityStore } from 'shared/stores/EntityStore';

export class JobApplicationStore extends EntityStore {
	constructor() {
		super('job-applications');
		makeObservable(this);
	}

	@observable jobApplications = [];
	@observable globalQuery = {};

	taskOptions = [
		{
			label: 'Candidate Submission',
			value: 'candidate_submission',
			occurredOnLabel: 'Submission Date'
		},
		{ label: 'Client Interview', value: 'client_interview' },
		{ label: 'Client Evaluation', value: 'client_assessment' },
		{ label: 'Pre Hiring Session', value: 'pre_hiring_session' },
		{ label: 'Hiring Request', value: 'hiring_request' },
		{ label: 'Offer Meeting', value: 'verbal_offer' },
		{ label: 'Offer Letter', value: 'offer_letter' },
		{ label: 'Onboarding Request', value: 'onboarding_request' },
		{
			label: 'Placement',
			value: 'placement',
			occurredOnLabel: 'Placement Date'
		},
		{ label: 'Note', value: 'note' }
	];

	schedulableTaskTypes = [
		'client_interview',
		'pre_hiring_session',
		'verbal_offer'
	];

	milestoneTaskTypes = ['candidate_submission', 'placement'];

	isMilestoneTaskType = type => this.milestoneTaskTypes.includes(type);

	getOccurredOnLabel = type => this.getTaskByValue(type)?.occurredOnLabel;

	taskStatusOptions = [
		{ label: 'Pending', value: 'pending' },
		{ label: 'Completed', value: 'completed' },
		{ label: 'Cancelled', value: 'cancelled' }
	];

	stageOptions = [
		{
			label: 'Internal Review',
			value: 'internal_review'
		},
		{
			label: 'Presentation',
			value: 'presented'
		},
		{
			label: 'Client Evaluation',
			value: 'client_assessment'
		},
		{
			label: 'Client Definition',
			value: 'client_definition'
		},
		{
			label: 'Pre Hiring',
			value: 'pre_hiring'
		},
		{
			label: 'Offer',
			value: 'offer'
		},
		{
			label: 'Onboarding',
			value: 'onboarding'
		},
		{
			label: 'Rejected by Client',
			value: 'rejected_by_client'
		},
		{
			label: 'Rejected by Recruiter',
			value: 'rejected_by_recruiter'
		},
		{
			label: 'Rejected by Manager',
			value: 'rejected_by_manager'
		},
		{
			label: 'On Hold by Talent',
			value: 'on_hold_by_talent'
		},
		{
			label: 'On Hold by Candidate',
			value: 'on_hold_by_candidate'
		},
		{
			label: 'On Hold by Client',
			value: 'on_hold_by_client'
		},
		{
			label: 'Unresponsive',
			value: 'unresponsive'
		},
		{
			label: 'Not Interested',
			value: 'not_interested'
		},
		{
			label: 'Hired',
			value: 'hired'
		}
	];

	statusOptions = [
		{ label: 'Active', value: 'active', color: '#717BBC' },
		{ label: 'Pre Hired', value: 'pre_hired', color: '#6172F3' },
		{ label: 'Rejected', value: 'rejected', color: '#F63D68' },
		{ label: 'On Hold', value: 'on_hold', color: '#F79009' },
		{ label: 'Withdrawn', value: 'withdrawn', color: '#A4A7AE' },
		{ label: 'Hired', value: 'hired', color: '#25BD9F' }
	];

	stageToStatus = {
		internal_review: 'active',
		presented: 'active',
		client_assessment: 'active',
		client_definition: 'active',
		pre_hiring: 'pre_hired',
		offer: 'pre_hired',
		onboarding: 'pre_hired',
		rejected_by_client: 'rejected',
		rejected_by_recruiter: 'rejected',
		rejected_by_manager: 'rejected',
		on_hold_by_talent: 'on_hold',
		on_hold_by_candidate: 'on_hold',
		on_hold_by_client: 'on_hold',
		unresponsive: 'withdrawn',
		not_interested: 'withdrawn',
		hired: 'hired'
	};

	defaultTableColumnsOptions = [
		{ label: 'Name', value: 'name' },
		{ label: 'Internal Stage', value: 'internal_stage' },
		{ label: 'Application Stage', value: 'stage' },
		{ label: 'Latest Task', value: 'latest_task' },
		{
			label: 'Expected Compensation',
			value: 'expected_compensation'
		},
		{
			label: 'Seniority',
			value: 'seniority'
		},
		{ label: 'English Level', value: 'english_level' },
		{
			label: 'Country',
			value: 'country'
		},
		{
			label: 'Owner',
			value: 'owner'
		}
	];

	tableColumnsOptions = [
		{ label: 'Name', value: 'name' },
		{ label: 'Internal Stage', value: 'internal_stage' },
		{ label: 'Application Stage', value: 'stage' },
		{ label: 'Latest Task', value: 'latest_task' },
		{
			label: 'Current Compensation',
			value: 'current_compensation'
		},
		{
			label: 'Expected Compensation',
			value: 'expected_compensation'
		},
		{
			label: 'Seniority',
			value: 'seniority'
		},
		{ label: 'English Level', value: 'english_level' },
		{
			label: 'Country',
			value: 'country'
		},
		{
			label: 'Owner',
			value: 'owner'
		},
		{
			label: 'Source',
			value: 'source'
		},
		{ label: 'Created At', value: 'created_at' },
		{ label: 'Last Engagement Date', value: 'last_engagement_date' }
	];

	getTaskByValue = value => {
		return this.taskOptions.find(task => task.value === value);
	};

	@action
	async loadJobApplications(reset = false) {
		const result = await this.search(reset);
		runInAction(() => {
			this.jobApplications = result.data.rows;
		});
	}

	@override
	setQuery(key, value) {
		this.query = { ...this.query, [key]: value };
	}

	@action
	setGlobalQuery(key, value) {
		this.globalQuery = { ...this.globalQuery, [key]: value };
	}

	@action
	resetGlobalQuery() {
		this.globalQuery = {};
	}

	@action
	async addApplication(request) {
		const result = await api.post(`${this.entityPluralEndpoint}`, request);
		return result;
	}

	@action
	async processApplication(request) {
		const result = await api.post(
			`${this.entityPluralEndpoint}/process`,
			request
		);
		return result;
	}

	@action
	async removeApplication(jobId) {
		const result = await api.delete(
			`${this.entityPluralEndpoint}/jobs/${jobId}`
		);
		return result;
	}

	@action
	async getAllByApplicantId(applicantId) {
		const result = await api.get(
			`${this.entityPluralEndpoint}/applicants/${applicantId}`
		);
		return result;
	}

	@action
	async getJobStats(jobId) {
		const result = await api.get(
			`${this.entityPluralEndpoint}/jobs/${jobId}/stats`
		);
		return result;
	}

	@action
	async createTask(jobApplicationId, request) {
		const result = await api.post(
			`${this.entityPluralEndpoint}/${jobApplicationId}/tasks`,
			request
		);
		return result;
	}

	@action
	async updateTask(jobApplicationId, taskId, request) {
		const result = await api.put(
			`${this.entityPluralEndpoint}/${jobApplicationId}/tasks/${taskId}`,
			request
		);
		return result;
	}

	@action
	async pinTask(jobApplicationId, taskId, request) {
		const result = await api.put(
			`${this.entityPluralEndpoint}/${jobApplicationId}/tasks/${taskId}/pin`,
			request
		);
		return result;
	}

	@action
	async getTasks(request) {
		const result = await api.post(
			`${this.entityPluralEndpoint}/tasks`,
			request
		);
		return result;
	}

	@action
	async deleteTask(jobApplicationId, taskId) {
		const result = await api.delete(
			`${this.entityPluralEndpoint}/${jobApplicationId}/tasks/${taskId}`
		);
		return result;
	}

	@action
	async bulkUpdate(ids, jobApplication) {
		const result = await api.post(`${this.entityPluralEndpoint}/bulk/update`, {
			ids,
			jobApplication
		});
		return result;
	}

	@action
	async bulkDelete(ids) {
		const result = await api.post(`${this.entityPluralEndpoint}/bulk/delete`, {
			ids
		});
		return result;
	}
}

export const jobApplicationStore = new JobApplicationStore();
